import { Components, Theme } from '@mui/material/styles'

/**
 * ## Two `primary.contrastText`, both wrong, and the second is the odd one out
 *
 * `contrastText` is the colour legible ON the primary colour, i.e. on a
 * primary-FILLED surface. A floating input label is never on one: at rest it sits
 * inside the field's own pale fill, and once shrunk it sits on
 * `background.default`, which this rule sets itself two lines further down.
 * `primary.main` defaults to BLACK (`./palette.ts`), so MUI computes `contrastText`
 * as `#fff` — verified in the browser, every rule derived from it carries the
 * literal `color:#fff`.
 *
 * 1. **Rest** (`&.MuiInputLabel-root`) — the label as the field's visible name,
 *    before anything is typed. White on the field fill.
 * 2. **Filled AND focused** (`&.MuiFormLabel-filled > &.Mui-focused`) — this one is
 *    self-evidently unintended: ten lines above, `&.Mui-focused, &.MuiFormLabel-filled`
 *    already sets `text.primary` **and** paints `background: background.default`
 *    behind the shrunk label. The nested rule then out-specifies that and repaints
 *    the text white on the background the sibling just gave it. The sibling states
 *    the intent; white was the anomaly. Both are now `text.primary` and the nested
 *    rule is kept explicit so the diff shows where the anomaly was.
 *
 * ## Why `text.primary` and not `text.secondary` for the resting label
 *
 * A resting floating label reads as a placeholder, and this app's real placeholders
 * measure `rgb(112,112,112)` (`text.secondary`), so that was the tempting choice.
 * Rejected: `text.secondary` is a HARDCODED `#707070` with no relationship to the
 * tenant's surface, which is the same class of unguaranteed colour that produced
 * this bug. `text.primary` is `theme.text_color || '#000000'` — the one palette
 * entry the tenant designated as readable on their own background. A resting label
 * rendering at full strength is a cosmetic nit; an unreadable one on a themed
 * tenant is the failure being removed. `MuiFormLabel-filled` keeps its `grey[400]`,
 * so the shrunk-but-unfocused state stays deliberately quiet.
 *
 * ## Live consumers exist — but I could not get one on screen
 *
 * This block is scoped to `&.MuiInputLabel-root`, and MUI's `<TextField label=…>`
 * renders one. Live sites: `UI/EmailPopup` and `pages/SignIn` (both imported by
 * `pages/CertificatePage`) and `Fields/Email`'s `FieldEmail`. (`Fields/SelectField`
 * and `Fields/SelectCountry` also render labels but are imported from nowhere.)
 * On every surface I could drive — certificates list, public certificate page,
 * access gate, `/create-account`, `/sign-in` — `.MuiInputLabel-root` matched 0
 * elements, because those sites are behind an owner session or a later step of the
 * OTP flow that this session could not reach. So: the declaration is wrong and the
 * consumers are real, but I am NOT claiming to have watched a white label.
 */
export const MuiFormLabel: Components<Theme>['MuiFormLabel'] = {
  styleOverrides: {
    root: ({ theme }) => ({
      '&.MuiInputLabel-root': {
        top: '12px',
        left: 0,
        color: theme.palette.text.primary,
        '&.Mui-focused, &.MuiFormLabel-filled': {
          color: theme.palette.text.primary,
          top: '17px',
          left: 0,
          padding: '0 4px',
          background: theme.palette.background.default,
        },
        '&.MuiFormLabel-filled': {
          color: theme.palette.grey[400],
          '&.Mui-focused': {
            color: theme.palette.text.primary,
          },
        },
        '&.Mui-error': {
          color: theme.palette.error.main,
          '&.Mui-focused': {
            color: theme.palette.error.main,
          },
        },
      },
    }),
  },
}
